/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import Dexie from 'dexie'
import { BaseAdapter } from './base.adapter.js'

/**
 * Dexie Adapter for Web Platform
 * Provides a SQLite-like interface using IndexedDB via Dexie.js
 * 
 * MIGRATION INFO:
 * - Baseline Version: 8 (established Dec 2025, client testing start)
 * - All schema changes before v8 are consolidated into baseline
 * - Future migrations: see docs/WEB_MIGRATIONS_DEXIE.md
 */
export class DexieAdapter extends BaseAdapter {
  constructor() {
    super()
    this.db = null
    this.DB_NAME = 'digiziege'
    this.DB_VERSION = 11 // Updated to v11 (Jan 2026): eartag is now optional
  }

  /**
   * Initialize Dexie database (define schema + open DB)
   * @returns {Promise<void>}
   */
  async initialize() {
    try {
      this.db = new Dexie(this.DB_NAME)

      // =================================================================
      // VERSION 8 - BASELINE (December 2025)
      // This is the starting point for client testing.
      // All previous development schema changes are consolidated here.
      // =================================================================
      this.db
        .version(8)
        .stores({
          groups: '++id,&name,location,createdAt,updatedAt',
          goats: '++id,groupId,&eartag,breed,gender,dateOfBirth,createdAt,updatedAt,notes',
          weights: '++id,goatId,measuredAt,createdAt,updatedAt',
          temperatures: '++id,goatId,measuredAt,createdAt,updatedAt',
          famacha: '++id,goatId,measuredAt,createdAt,updatedAt',
          dag: '++id,goatId,measuredAt,createdAt,updatedAt'
        })
        // No upgrade callback needed for baseline version

      // =================================================================
      // FUTURE MIGRATIONS - Add new versions below
      // See docs/WEB_MIGRATIONS_DEXIE.md for migration guide
      // =================================================================
      // =================================================================
      // VERSION 9 - Notes Array → String (January 2026)
      // Migration: Convert goat notes from array format to single string
      // =================================================================
      this.db
        .version(9)
        .stores({
          // Schema unchanged from v8 (notes field type is flexible in IndexedDB)
          groups: '++id,&name,location,createdAt,updatedAt',
          goats: '++id,groupId,&eartag,breed,gender,dateOfBirth,createdAt,updatedAt,notes',
          weights: '++id,goatId,measuredAt,createdAt,updatedAt',
          temperatures: '++id,goatId,measuredAt,createdAt,updatedAt',
          famacha: '++id,goatId,measuredAt,createdAt,updatedAt',
          dag: '++id,goatId,measuredAt,createdAt,updatedAt'
        })
        .upgrade(async (trans) => {
          console.log('[Dexie v8→v9] Migrating goat notes from array to string...')
          
          const goats = await trans.table('goats').toArray()
          let migratedCount = 0
          
          for (const goat of goats) {
            if (Array.isArray(goat.notes)) {
              // Convert array of note objects to newline-separated string
              const newNotes = goat.notes
                .map(note => note?.text || '')
                .filter(text => text.trim())
                .join('\n') || null
              
              await trans.table('goats').update(goat.id, { notes: newNotes })
              migratedCount++
            }
          }
          
          console.log(`[Dexie v8→v9] Migration complete: ${migratedCount} goats updated`)
        })

      // =================================================================
      // VERSION 10 - Added health_records table (January 2026)
      // Migration: Add new table for tracking wounds, diseases, treatments
      // =================================================================
      this.db
        .version(10)
        .stores({
          groups: '++id,&name,location,createdAt,updatedAt',
          goats: '++id,groupId,&eartag,breed,gender,dateOfBirth,createdAt,updatedAt,notes',
          weights: '++id,goatId,measuredAt,createdAt,updatedAt',
          temperatures: '++id,goatId,measuredAt,createdAt,updatedAt',
          famacha: '++id,goatId,measuredAt,createdAt,updatedAt',
          dag: '++id,goatId,measuredAt,createdAt,updatedAt',
          health_records: '++id,goatId,type,measuredAt,createdAt,updatedAt'
        })
        .upgrade(async (trans) => {
          console.log('Migrating database to v10: Adding health_records table...')
          // No data transformation needed - new table starts empty
          console.log('Migration to v10 complete')
        })

      // =================================================================
      // VERSION 11 - Eartag is now optional (January 2026)
      // Migration: Remove unique constraint from eartag, keep as indexed field
      // =================================================================
      this.db
        .version(11)
        .stores({
          groups: '++id,&name,location,createdAt,updatedAt',
          goats: '++id,groupId,eartag,breed,gender,dateOfBirth,createdAt,updatedAt,notes', // Removed & from eartag
          weights: '++id,goatId,measuredAt,createdAt,updatedAt',
          temperatures: '++id,goatId,measuredAt,createdAt,updatedAt',
          famacha: '++id,goatId,measuredAt,createdAt,updatedAt',
          dag: '++id,goatId,measuredAt,createdAt,updatedAt',
          health_records: '++id,goatId,type,measuredAt,createdAt,updatedAt'
        })
        .upgrade(async (trans) => {
          console.log('Migrating database to v11: Eartag is now optional (removed unique constraint)')
          // No data transformation needed - just schema change
          // IndexedDB will continue to work with existing data
          console.log('Migration to v11 complete')
        })

      // Track migration events
      this.db.on('versionchange', (event) => {
        console.warn('Database version change detected:', event)
        console.warn('Another tab may have updated the database')
      })

      await this.db.open()
      
      const currentVersion = this.db.verno
      console.log(`Dexie (IndexedDB) initialized successfully at version ${currentVersion}`)
      
      // Log if migration occurred
      if (currentVersion !== this.DB_VERSION) {
        console.warn(`Version mismatch: Expected ${this.DB_VERSION}, got ${currentVersion}`)
      }
    } catch (error) {
      console.error('Dexie initialization failed:', error)
      
      // Log specific migration errors
      if (error.name === 'UpgradeError') {
        console.error('Migration failed during database upgrade')
      } else if (error.name === 'VersionError') {
        console.error('Database version conflict detected')
      }
      
      throw error
    }
  }
  /**
   * Execute a SELECT-like query
   * Translates SQL syntax to Dexie.js API calls for IndexedDB
   * Examples:
   *   'SELECT * FROM goats' → db.goats.toArray()
   *   'SELECT * FROM goats WHERE id = ?' → db.goats.get(id)
   * @param {string} sql - SQL query string
   * @param {Array} params - Query parameters (?, ?, ...)
   * @returns {Promise<{values: Array}>} Results in SQLite-compatible format
   */
  async query(sql, params = []) {
    try {
      const sqlLower = sql.toLowerCase().trim()

      // ------------------------------------------
      // GROUPS QUERIES
      // ------------------------------------------
      if (sqlLower.includes('select') && sqlLower.includes('from groups')) {
        let results = []

        if (sqlLower.includes('where id = ?')) {
          const group = await this.db.groups.get(params[0])
          results = group ? [group] : []
        } else if (
          sqlLower.includes('order by created_at desc') ||
          sqlLower.includes('order by id desc')
        ) {
          results = await this.db.groups.orderBy('id').reverse().toArray()
        } else {
          results = await this.db.groups.toArray()
        }

        return {
          values: results.map(item => ({
            id: item.id,
            name: item.name,
            location: item.location,
            description: item.description,
            created_at: item.createdAt,
            updated_at: item.updatedAt
          }))
        }
      }

      // ------------------------------------------
      // GOAT COUNT BY GROUP
      //  SELECT COUNT(*) FROM goats WHERE group_id = ?
      // ------------------------------------------
      if (
        sqlLower.includes('count(*)') &&
        sqlLower.includes('from goats') &&
        sqlLower.includes('where group_id = ?')
      ) {
        const count = await this.db.goats
          .where('groupId')
          .equals(params[0])
          .count()

        return { values: [{ count }] }
      }

      // ------------------------------------------
      // GOATS QUERIES
      // ------------------------------------------
      if (sqlLower.includes('select') && sqlLower.includes('from goats')) {
        let results = []

        if (sqlLower.includes('where id = ?')) {
          const goat = await this.db.goats.get(params[0])
          results = goat ? [goat] : []
        } else if (sqlLower.includes('where group_id = ?')) {
          results = await this.db.goats
            .where('groupId')
            .equals(params[0])
            .toArray()
        } else if (
          sqlLower.includes('order by created_at desc') ||
          sqlLower.includes('order by id desc')
        ) {
          results = await this.db.goats.orderBy('id').reverse().toArray()
        } else {
          results = await this.db.goats.toArray()
        }

        return {
          values: results.map(item => ({
          id: item.id,
          name: item.name,
          date_of_birth: item.dateOfBirth,
          place_of_birth: item.placeOfBirth,
          breed: item.breed,
          gender: item.gender,
          group_id: item.groupId,
          notes: item.notes || null,
          eartag: item.eartag ?? null,
          created_at: item.createdAt,
          updated_at: item.updatedAt
         }))
        }

      }

      // ------------------------------------------
      // WEIGHTS QUERIES
      //  SELECT * FROM weights WHERE goat_id = ? ...
      //  SELECT * FROM weights
      // ------------------------------------------
      if (
        sqlLower.includes('select') &&
        sqlLower.includes('from weights')
      ) {
        // SELECT by goat_id
        if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]

          let rows = await this.db.weights.where('goatId').equals(goatId).toArray()
          rows = rows.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

          if (sqlLower.includes('limit') && params.length >= 2) {
            const limit = params[1]
            rows = rows.slice(0, limit)
          }

          return {
            values: rows.map(w => ({
              id: w.id,
              goat_id: w.goatId,
              value: w.value,
              measured_at: w.measuredAt,
              created_at: w.createdAt,
              updated_at: w.updatedAt
            }))
          }
        }

        // SELECT ALL
        let rows = await this.db.weights.toArray()
        return {
          values: rows.map(w => ({
            id: w.id,
            goat_id: w.goatId,
            value: w.value,
            measured_at: w.measuredAt,
            created_at: w.createdAt,
            updated_at: w.updatedAt
          }))
        }
      }

      // ------------------------------------------
      // TEMPERATURES QUERIES
      //  SELECT * FROM temperatures WHERE goat_id = ? ...
      //  SELECT * FROM temperatures
      // ------------------------------------------
      if (
        sqlLower.includes('select') &&
        sqlLower.includes('from temperatures')
      ) {
        // SELECT by goat_id
        if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]

          let rows = await this.db.temperatures.where('goatId').equals(goatId).toArray()
          rows = rows.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

          if (sqlLower.includes('limit') && params.length >= 2) {
            const limit = params[1]
            rows = rows.slice(0, limit)
          }

          return {
            values: rows.map(t => ({
              id: t.id,
              goat_id: t.goatId,
              value: t.value,
              measured_at: t.measuredAt,
              created_at: t.createdAt,
              updated_at: t.updatedAt
            }))
          }
        }

        // SELECT ALL
        let rows = await this.db.temperatures.toArray()
        return {
          values: rows.map(t => ({
            id: t.id,
            goat_id: t.goatId,
            value: t.value,
            measured_at: t.measuredAt,
            created_at: t.createdAt,
            updated_at: t.updatedAt
            }))
        }
      }
      // FAMACHA QUERIES
      //  SELECT * FROM famacha WHERE goat_id = ? ORDER BY created_at DESC [LIMIT ?]
      //  SELECT * FROM famacha WHERE id = ?
      //  SELECT * FROM famacha ORDER BY created_at DESC
      // ------------------------------------------
      if (
        sqlLower.includes('select') &&
        sqlLower.includes('from famacha')
      ) {
        // SELECT by goat_id
        if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          let rows = await this.db.famacha.where('goatId').equals(goatId).toArray()
          rows = rows.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

          if (sqlLower.includes('limit') && params.length >= 2) {
            const limit = params[1]
            rows = rows.slice(0, limit)
          }

          return {
            values: rows.map(f => ({
              id: f.id,
              goat_id: f.goatId,
              score: f.score,
              measured_at: f.measuredAt,
              created_at: f.createdAt,
              updated_at: f.updatedAt
            }))
          }
        }

        // SELECT by id
        if (sqlLower.includes('where id = ?')) {
          const id = params[0]
          const row = await this.db.famacha.get(id)
          
          if (!row) {
            return { values: [] }
          }

          return {
            values: [{
              id: row.id,
              goat_id: row.goatId,
              score: row.score,
              measured_at: row.measuredAt,
              created_at: row.createdAt,
              updated_at: row.updatedAt
            }]
          }
        }

        // SELECT all
        let rows = await this.db.famacha.toArray()
        rows = rows.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

        return {
          values: rows.map(f => ({
            id: f.id,
            goat_id: f.goatId,
            score: f.score,
            measured_at: f.measuredAt,
            created_at: f.createdAt,
            updated_at: f.updatedAt
          }))
        }
      }

            // DAG QUERIES
      //  SELECT * FROM dag WHERE goat_id = ? ORDER BY created_at DESC [LIMIT ?]
      //  SELECT * FROM dag WHERE id = ?
      //  SELECT * FROM dag ORDER BY created_at DESC
      // ------------------------------------------
      if (
        sqlLower.includes('select') &&
        sqlLower.includes('from dag')
      ) {
        // SELECT by goat_id
        if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          let rows = await this.db.dag.where('goatId').equals(goatId).toArray()
          rows = rows.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

          if (sqlLower.includes('limit') && params.length >= 2) {
            const limit = params[1]
            rows = rows.slice(0, limit)
          }

          return {
            values: rows.map(d => ({
              id: d.id,
              goat_id: d.goatId,
              score: d.score,
              measured_at: d.measuredAt,
              created_at: d.createdAt,
              updated_at: d.updatedAt
            }))
          }
        }

        // SELECT by id
        if (sqlLower.includes('where id = ?')) {
          const id = params[0]
          const row = await this.db.dag.get(id)

          if (!row) {
            return { values: [] }
          }

          return {
            values: [{
              id: row.id,
              goat_id: row.goatId,
              score: row.score,
              measured_at: row.measuredAt,
              created_at: row.createdAt,
              updated_at: row.updatedAt
            }]
          }
        }

        // SELECT all
        let rows = await this.db.dag.toArray()
        rows = rows.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

        return {
          values: rows.map(d => ({
            id: d.id,
            goat_id: d.goatId,
            score: d.score,
            measured_at: d.measuredAt,
            created_at: d.createdAt,
            updated_at: d.updatedAt
          }))
        }
      }

      // ------------------------------------------
      // HEALTH_RECORDS QUERIES
      //  SELECT * FROM health_records WHERE goat_id = ? ORDER BY measured_at DESC
      //  SELECT * FROM health_records WHERE id = ?
      //  SELECT * FROM health_records WHERE goat_id = ? AND type IN ('wound', 'disease', 'injury') ...
      //  SELECT * FROM health_records ORDER BY measured_at DESC
      // ------------------------------------------
      if (
        sqlLower.includes('select') &&
        sqlLower.includes('from health_records')
      ) {
        // SELECT by id
        if (sqlLower.includes('where id = ?')) {
          const id = params[0]
          const row = await this.db.health_records.get(id)
          
          if (!row) {
            return { values: [] }
          }

          return {
            values: [{
              id: row.id,
              goat_id: row.goatId,
              type: row.type,
              title: row.title,
              description: row.description,
              notes: row.notes,
              measured_at: row.measuredAt,
              created_at: row.createdAt,
              updated_at: row.updatedAt
            }]
          }
        }

        // SELECT by goat_id with type filter (wounds)
        if (sqlLower.includes('where goat_id = ?') && sqlLower.includes("type in ('wound'")) {
          const goatId = params[0]
          let rows = await this.db.health_records.where('goatId').equals(goatId).toArray()
          rows = rows.filter(r => ['wound', 'disease', 'injury'].includes(r.type))
          rows = rows.sort((a, b) => {
            const dateA = new Date(a.measuredAt)
            const dateB = new Date(b.measuredAt)
            if (dateB - dateA !== 0) return dateB - dateA
            return new Date(b.createdAt) - new Date(a.createdAt)
          })

          return {
            values: rows.map(r => ({
              id: r.id,
              goat_id: r.goatId,
              type: r.type,
              title: r.title,
              description: r.description,
              notes: r.notes,
              measured_at: r.measuredAt,
              created_at: r.createdAt,
              updated_at: r.updatedAt
            }))
          }
        }

        // SELECT by goat_id with type filter (treatments)
        if (sqlLower.includes('where goat_id = ?') && sqlLower.includes("type in ('treatment'")) {
          const goatId = params[0]
          let rows = await this.db.health_records.where('goatId').equals(goatId).toArray()
          rows = rows.filter(r => ['treatment', 'vaccination'].includes(r.type))
          rows = rows.sort((a, b) => {
            const dateA = new Date(a.measuredAt)
            const dateB = new Date(b.measuredAt)
            if (dateB - dateA !== 0) return dateB - dateA
            return new Date(b.createdAt) - new Date(a.createdAt)
          })

          return {
            values: rows.map(r => ({
              id: r.id,
              goat_id: r.goatId,
              type: r.type,
              title: r.title,
              description: r.description,
              notes: r.notes,
              measured_at: r.measuredAt,
              created_at: r.createdAt,
              updated_at: r.updatedAt
            }))
          }
        }

        // SELECT by goat_id (all health records for a goat)
        if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          let rows = await this.db.health_records.where('goatId').equals(goatId).toArray()
          rows = rows.sort((a, b) => {
            const dateA = new Date(a.measuredAt)
            const dateB = new Date(b.measuredAt)
            if (dateB - dateA !== 0) return dateB - dateA
            return new Date(b.createdAt) - new Date(a.createdAt)
          })

          return {
            values: rows.map(r => ({
              id: r.id,
              goat_id: r.goatId,
              type: r.type,
              title: r.title,
              description: r.description,
              notes: r.notes,
              measured_at: r.measuredAt,
              created_at: r.createdAt,
              updated_at: r.updatedAt
            }))
          }
        }

        // SELECT all
        let rows = await this.db.health_records.toArray()
        rows = rows.sort((a, b) => {
          const dateA = new Date(a.measuredAt)
          const dateB = new Date(b.measuredAt)
          if (dateB - dateA !== 0) return dateB - dateA
          return new Date(b.createdAt) - new Date(a.createdAt)
        })

        return {
          values: rows.map(r => ({
            id: r.id,
            goat_id: r.goatId,
            type: r.type,
            title: r.title,
            description: r.description,
            notes: r.notes,
            measured_at: r.measuredAt,
            created_at: r.createdAt,
            updated_at: r.updatedAt
          }))
        }
      }


      throw new Error(`Unsupported query: ${sql}`)
    } catch (error) {
      console.error('Dexie query error:', error)
      throw error
    }
  }

  /**
   * Execute INSERT / UPDATE / DELETE
   * Translates SQL mutations to Dexie.js API calls
   * Examples:
   *   INSERT → db.goats.add(data)
   *   UPDATE → db.goats.update(id, data)
   *   DELETE → db.goats.delete(id)
   * @param {string} sql - SQL command string
   * @param {Array} params - Command parameters (?, ?, ...)
   * @returns {Promise<{changes: {lastId?: number, changes?: number}}>} SQLite-compatible result
   */
  async run(sql, params = []) {
    try {
      const sqlLower = sql.toLowerCase().trim()

      // ------------------------------------------
      // GROUPS OPERATIONS
      // ------------------------------------------

      // INSERT INTO groups (name, location, description)
      if (sqlLower.startsWith('insert into groups')) {
        const group = {
          name: params[0],
          location: params[1] || null,
          description: params[2] || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        const id = await this.db.groups.add(group)
        return { changes: { lastId: id } }
      }

      // UPDATE groups SET ... WHERE id = ?
      if (sqlLower.startsWith('update groups')) {
        const id = params[params.length - 1]
        const updates = {
          name: params[0],
          location: params[1] || null,
          description: params[2] || null,
          updatedAt: new Date().toISOString()
        }

        await this.db.groups.update(id, updates)
        return { changes: { changes: 1 } }
      }

      // DELETE FROM groups [WHERE id = ?]
      if (sqlLower.startsWith('delete from groups')) {
        if (params.length === 0) {
          await this.db.groups.clear()
          return { changes: { changes: 0 } }
        } else {
          await this.db.groups.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }

      // ------------------------------------------
      // GOATS OPERATIONS
      // ------------------------------------------

      // INSERT INTO goats (...)
      if (sqlLower.startsWith('insert into goats')) {
         const notes = params[7] || null;
         const now = new Date().toISOString();

         const goat = {
            name: params[0] || null,
            dateOfBirth: params[1],
            placeOfBirth: params[2] || null,
            breed: params[3],
            gender: params[4],
            groupId: params[5] || null,
            eartag: params[6] || null, // Now optional
             notes,             
             createdAt: now,
             updatedAt: now
            };

          const id = await this.db.goats.add(goat)
          return { changes: { lastId: id } }
        }


      // UPDATE goats ...
      if (sqlLower.startsWith('update goats')) {
        // Bulk case: "SET group_id = NULL WHERE group_id = ?"
        if (
          sqlLower.includes('where group_id = ?') &&
          sqlLower.includes('set group_id = null')
        ) {
          const groupId = params[0]
          const goatsToUpdate = await this.db.goats
            .where('groupId')
            .equals(groupId)
            .toArray()

          let changed = 0
          for (const g of goatsToUpdate) {
            const ok = await this.db.goats.update(g.id, {
              groupId: null,
              updatedAt: new Date().toISOString()
            })
            if (ok) changed++
          }

          return { changes: { changes: changed } }
        }

        // Standard update by ID
        const id = params[params.length - 1]
        const notes = params[7];
        const updates = {
          name: params[0] || null,
          dateOfBirth: params[1],
          placeOfBirth: params[2] || null,
          breed: params[3],
          gender: params[4],
          groupId: params[5] || null,
          eartag: params[6], // Required field
          updatedAt: new Date().toISOString()
        }
        if (notes !== undefined) {
          updates.notes = notes || null;
        }

        await this.db.goats.update(id, updates)
        return { changes: { changes: 1 } }
      }

      // DELETE FROM goats [WHERE id = ?]
      if (sqlLower.startsWith('delete from goats')) {
        if (params.length === 0) {
          await this.db.goats.clear()
          return { changes: { changes: 0 } }
        } else {
          await this.db.goats.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }

      // ------------------------------------------
      // WEIGHTS OPERATIONS
      // ------------------------------------------

      // INSERT INTO weights (goat_id, value) or (goat_id, value, measured_at)
      if (sqlLower.startsWith('insert into weights')) {
        const now = new Date().toISOString()
        const [goatId, value, measuredAt] = params

        const id = await this.db.weights.add({
          goatId,
          value,
          measuredAt: measuredAt || now,
          createdAt: now,
          updatedAt: now
        })

        return { changes: { lastId: id } }
      }

      // UPDATE weights SET value = ?, measured_at = ?, updated_at = ... WHERE id = ?
      if (sqlLower.startsWith('update weights')) {
        // Params: [value, measuredAt (optional), id]
        const hasthree = params.length === 3
        const value = params[0]
        const measuredAt = hasthree ? params[1] : undefined
        const id = hasthree ? params[2] : params[1]

        const updates = {
          value,
          updatedAt: new Date().toISOString()
        }
        if (measuredAt !== undefined) {
          updates.measuredAt = measuredAt
        }

        const ok = await this.db.weights.update(id, updates)

        return { changes: { changes: ok ? 1 : 0 } }
      }

      // DELETE FROM weights [WHERE id = ?] [WHERE goat_id = ?]
      if (sqlLower.startsWith('delete from weights')) {
        if (params.length === 0) {
          await this.db.weights.clear()
          return { changes: { changes: 0 } }
        } else if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          const recordsToDelete = await this.db.weights.where('goatId').equals(goatId).toArray()
          await this.db.weights.where('goatId').equals(goatId).delete()
          return { changes: { changes: recordsToDelete.length } }
        } else {
          await this.db.weights.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }

      // ------------------------------------------
      // TEMPERATURES OPERATIONS
      // ------------------------------------------

      // INSERT INTO temperatures (goat_id, value) or (goat_id, value, measured_at)
      if (sqlLower.startsWith('insert into temperatures')) {
        const now = new Date().toISOString()
        const [goatId, value, measuredAt] = params

        const id = await this.db.temperatures.add({
          goatId,
          value,
          measuredAt: measuredAt || now,
          createdAt: now,
          updatedAt: now
        })
        return { changes: { lastId: id } }
      }

      // UPDATE temperatures SET value = ?, measured_at = ?, updated_at = ... WHERE id = ?
      if (sqlLower.startsWith('update temperatures')) {
        // Params: [value, measuredAt (optional), id]
        const hasThree = params.length === 3
        const value = params[0]
        const measuredAt = hasThree ? params[1] : undefined
        const id = hasThree ? params[2] : params[1]

        const updates = {
          value,
          updatedAt: new Date().toISOString()
        }
        if (measuredAt !== undefined) {
          updates.measuredAt = measuredAt
        }

        const ok = await this.db.temperatures.update(id, updates)

        return { changes: { changes: ok ? 1 : 0 } }
      }
      
      // DELETE FROM temperatures [WHERE id = ?] [WHERE goat_id = ?]
      if (sqlLower.startsWith('delete from temperatures')) {
        if (params.length === 0) {
          await this.db.temperatures.clear()
          return { changes: { changes: 0 } }
        } else if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          const recordsToDelete = await this.db.temperatures.where('goatId').equals(goatId).toArray()
          await this.db.temperatures.where('goatId').equals(goatId).delete()
          return { changes: { changes: recordsToDelete.length } }
        } else {
          await this.db.temperatures.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }

      //------------------------------------------
      // FAMACHA OPERATIONS
      // ------------------------------------------

      // INSERT INTO famacha (goat_id, score) or (goat_id, score, measured_at)
      if (sqlLower.startsWith('insert into famacha')) {
        const now = new Date().toISOString()
        const [goatId, score, measuredAt] = params

        const id = await this.db.famacha.add({
          goatId,
          score,
          measuredAt: measuredAt || now,
          createdAt: now,
          updatedAt: now
        })

        return { changes: { lastId: id } }
      }

      // UPDATE famacha SET score = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?
      if (sqlLower.startsWith('update famacha')) {
        // Params: [score, measuredAt (optional), id]
        const hasThree = params.length === 3
        const score = params[0]
        const measuredAt = hasThree ? params[1] : undefined
        const id = hasThree ? params[2] : params[1]

        const updates = {
          score,
          updatedAt: new Date().toISOString()
        }
        if (measuredAt !== undefined) {
          updates.measuredAt = measuredAt
        }

        const ok = await this.db.famacha.update(id, updates)

        return { changes: { changes: ok ? 1 : 0 } }
      }

      // DELETE FROM famacha [WHERE id = ?] [WHERE goat_id = ?]
      if (sqlLower.startsWith('delete from famacha')) {
        if (params.length === 0) {
          await this.db.famacha.clear()
          return { changes: { changes: 0 } }
        } else if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          const recordsToDelete = await this.db.famacha.where('goatId').equals(goatId).toArray()
          await this.db.famacha.where('goatId').equals(goatId).delete()
          return { changes: { changes: recordsToDelete.length } }
        } else {
          await this.db.famacha.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }

      //------------------------------------------
      // DAG OPERATIONS
      //------------------------------------------

      // INSERT INTO dag (goat_id, score) or (goat_id, score, measured_at)
      if (sqlLower.startsWith('insert into dag')) {
        const now = new Date().toISOString()
        const [goatId, score, measuredAt] = params

        const id = await this.db.dag.add({
          goatId,
          score,
          measuredAt: measuredAt || now,
          createdAt: now,
          updatedAt: now
        })

        return { changes: { lastId: id } }
      }

      // UPDATE dag SET score = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?
      if (sqlLower.startsWith('update dag')) {
        // Params: [score, measuredAt (optional), id]
        const hasThree = params.length === 3
        const score = params[0]
        const measuredAt = hasThree ? params[1] : undefined
        const id = hasThree ? params[2] : params[1]

        const updates = {
          score,
          updatedAt: new Date().toISOString()
        }
        if (measuredAt !== undefined) {
          updates.measuredAt = measuredAt
        }

        const ok = await this.db.dag.update(id, updates)

        return { changes: { changes: ok ? 1 : 0 } }
      }

      // DELETE FROM dag [WHERE id = ?] [WHERE goat_id = ?]
      if (sqlLower.startsWith('delete from dag')) {
        if (params.length === 0) {
          await this.db.dag.clear()
          return { changes: { changes: 0 } }
        } else if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          const recordsToDelete = await this.db.dag.where('goatId').equals(goatId).toArray()
          await this.db.dag.where('goatId').equals(goatId).delete()
          return { changes: { changes: recordsToDelete.length } }
        } else {
          await this.db.dag.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }

      //------------------------------------------
      // HEALTH_RECORDS OPERATIONS
      //------------------------------------------

      // INSERT INTO health_records (goat_id, type, title, description, notes, measured_at)
      if (sqlLower.startsWith('insert into health_records')) {
        const now = new Date().toISOString()
        const [goatId, type, title, description, notes, measuredAt] = params

        const id = await this.db.health_records.add({
          goatId,
          type,
          title,
          description,
          notes: notes || null,
          measuredAt: measuredAt || now,
          createdAt: now,
          updatedAt: now
        })

        return { changes: { lastId: id } }
      }

      // UPDATE health_records SET type = ?, title = ?, description = ?, notes = ?, measured_at = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?
      if (sqlLower.startsWith('update health_records')) {
        const [type, title, description, notes, measuredAt, id] = params

        const updates = {
          type,
          title,
          description,
          notes: notes || null,
          measuredAt,
          updatedAt: new Date().toISOString()
        }

        const ok = await this.db.health_records.update(id, updates)

        return { changes: { changes: ok ? 1 : 0 } }
      }

      // DELETE FROM health_records WHERE id = ?
      // DELETE FROM health_records WHERE goat_id = ?
      if (sqlLower.startsWith('delete from health_records')) {
        if (params.length === 0) {
          await this.db.health_records.clear()
          return { changes: { changes: 0 } }
        } else if (sqlLower.includes('where goat_id = ?')) {
          const goatId = params[0]
          const recordsToDelete = await this.db.health_records.where('goatId').equals(goatId).toArray()
          await this.db.health_records.where('goatId').equals(goatId).delete()
          return { changes: { changes: recordsToDelete.length } }
        } else {
          await this.db.health_records.delete(params[0])
          return { changes: { changes: 1 } }
        }
      }


      throw new Error(`Unsupported SQL command: ${sql}`)
    } catch (error) {
      console.error('Dexie run error:', error)
      throw error
    }
  }

  /**
   * execute() is a no-op for Dexie; schema is defined in initialize()
   */
  async execute(_sql) {
    console.log(
      '️ Dexie: schema is defined during initialize(); execute() does nothing on web.'
    )
    return Promise.resolve()
  }

  /**
   * Close DB connection
   */
  async close() {
    if (this.db) {
      this.db.close()
      this.db = null
      console.log('Dexie connection closed')
    }
  }

  /**
   * Drop all tables and reset database schema
   * This deletes the entire IndexedDB database
   * @returns {Promise<void>}
   */
  async dropAllTables() {
    try {
      console.log('Dropping all tables from IndexedDB...')

      // Close the database first
      if (this.db) {
        this.db.close()
        this.db = null
      }

      // Delete the entire database
      await Dexie.delete(this.DB_NAME)

      console.log('IndexedDB database deleted successfully')
      console.log('Database will be recreated on next initialization')
    } catch (error) {
      console.error('Failed to drop IndexedDB database:', error)
      throw error
    }
  }

  /**
   * Adapter name
   */
  getName() {
    return 'Dexie (IndexedDB)'
  }
}

// Export singleton instance
export const dexieAdapter = new DexieAdapter()
